import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Price } from '@invertase/firestore-stripe-payments';
import { ModalController } from '@ionic/angular';
import { AuthService, LoadingComponent } from '@scandium-oy/ngx-scandium';
import { map, shareReplay, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { DashboardViewComponent } from 'src/app/components/views/dashboard/view-dashboard.component';
import { ContractorIntroDialogComponent } from 'src/app/contractors/contractor/intro/intro.dialog';
import { ContractorsService } from 'src/app/services/contractors.service';
import { DemoService } from 'src/app/services/demo.service';
import { LicenseService } from 'src/app/services/licence.service';
import { ProductsService } from 'src/app/services/products.service';
import { StripeService } from 'src/app/services/stripe.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { Roles } from 'src/app/utility/role';
import { getDateWithTimezone } from 'src/app/utility/time';
import { environment } from 'src/environments/environment';
import { DemoDialogComponent } from './demo/demo.dialog';

@Component({
  selector: 'app-stripe-order-page',
  templateUrl: './order.page.html',
  styleUrls: ['./order.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    DashboardViewComponent,
    LoadingComponent,
  ],
})
export class StripeOrderPage {
  private authService = inject(AuthService);
  private contractorService = inject(ContractorsService);
  private demoService = inject(DemoService);
  private licenseService = inject(LicenseService);
  private modalCtrl = inject(ModalController);
  private productsService = inject(ProductsService);
  private stripeService = inject(StripeService);
  private usersService = inject(UserSessionService);

  readonly telNumber = '+358 40 968 1882';
  error = signal<string>(null);
  loading = signal(false);

  isBillingAccount = this.licenseService.isBillingAccount;
  billingAccountName = this.licenseService.billingAccountName;

  adminProduct$ = this.productsService.getList({ role: Roles.admin }).pipe(
    map((items) => items.length > 0 ? items[0] : null),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  readonly orderUrl = environment.host;

  constructor() {
    effect(() => {
      const contractor = this.contractorService.contractorS();
      const user = this.usersService.currentUserS();
      if (contractor != null && user != null && contractor?.billingAccount == null && user?.role === Roles.admin) {
        this.contractorService.updateOnly(contractor.guid, { billingAccount: { guid: user.guid, name: user.displayName } });
      }
    });
  }

  async onOrder(price: Price & { guid: string }) {
    this.loading.set(true);
    const user = this.usersService.currentUserS();
    const taxRate = await this.stripeService.getTaxRate();
    this.stripeService.createSubscription(price, taxRate.id, user, null).then((session) => {
      const sub = session.subscribe((val) => {
        if (val?.url || val?.error) {
          this.loading.set(false);
          sub.unsubscribe();
          if (val.error) {
            this.error.set(val.error.message);
          }
          if (val.url) {
            if (Capacitor.isNativePlatform()) {
              Browser.addListener('browserFinished', () => {
                // TODO
              });
              Browser.open({ url: val.url });
            } else {
              window.location.assign(val.url);
            }
          }
        }
      });
    }).catch((e) => {
      this.loading.set(false);
      console.error(e);
    });
  }

  onIntro() {
    this.modalCtrl.create({ component: ContractorIntroDialogComponent }).then(async (m) => m.present());
  }

  onDemo() {
    this.modalCtrl.create({ component: DemoDialogComponent }).then((m) => {
      m.present();

      m.onDidDismiss<Date>().then((data) => {
        if (data.data) {
          this.demoService.reserve(getDateWithTimezone(data.data)).pipe(take(1)).subscribe();
        }
      });
    });
  }

  logout() {
    this.authService.signOut();
  }
}
